import {
    AiOutlineCalendar,
    AiOutlineClose,
    AiOutlineEdit,
    AiOutlineInfoCircle,
    AiOutlinePlus,
    AiOutlineReload,
    AiOutlineThunderbolt,
    AiOutlineWarning,
} from 'react-icons/ai';
import TextareaAutosize from 'react-textarea-autosize';
import {
    Box,
    BoxProps,
    Button,
    ButtonGroup,
    Center,
    chakra,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    Heading,
    HStack,
    Icon,
    IconButton,
    Image,
    Input,
    InputGroup,
    InputRightElement,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    SimpleGrid,
    StackDivider,
    Switch,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tag,
    TagLabel,
    TagCloseButton,
    Text,
    Textarea,
    Tooltip,
    VStack,
    Wrap,
    VisuallyHidden,
    BreadcrumbItem,
    BreadcrumbLink,
    Breadcrumb,
    Avatar,
} from '@chakra-ui/react';
import React from 'react';
import { BsArrowRight, BsArrowRightShort } from 'react-icons/bs';
import { BiChevronDown } from 'react-icons/bi';
import { Graphics } from '../../../../../config/svg';
import { ReportingViewBaseConfig } from '../../base';
import { TaskListViewProps } from './taskListProps';
import { RiAttachment2 } from 'react-icons/ri';
import { MdOutlineAlternateEmail, MdOutlineRateReview } from 'react-icons/md';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { HiOutlineSlash } from 'react-icons/hi2';
import { TaskItemContainerProps, TaskItemViewProps } from './item';
import { GoWorkflow } from 'react-icons/go';

export function createTaskListView(
    config: ReportingViewBaseConfig & {
        Container: {
            Item: React.FC<TaskItemContainerProps>;
        };
    }
): React.FC<TaskListViewProps> {
    const {
        Container: { Item: TaskItemContainer },
        Layout: {
            Centered: {
                Component: {
                    Root: LayoutRoot,
                    Header: LayoutHeader,
                    Content: LayoutContent,
                    Body: LayoutBody,
                },
            },
        },
        UI: {
            Application: { Link },
            Date: {
                DatePicker: {
                    Container: DateContainer,
                    Absolute: DateAbsolute,
                    Relative: DateRelative,
                },
            },
        },
    } = config;

    const TaskItem: React.FC<TaskItemViewProps> = (props) => {
        return (
            <VStack w="full" spacing={4}>
                <HStack w="full" justify="space-between">
                    <VStack align="start">
                        <HStack spacing={3}>
                            <Icon
                                as={props.status.Icon}
                                fontSize="lg"
                                color={`${props.status.colorScheme}.300`}
                            />
                            <Text fontWeight="semibold" noOfLines={1}>
                                {props.title}
                            </Text>
                        </HStack>
                        {props.description && (
                            <Text color="whiteAlpha.700" noOfLines={2}>
                                {props.description}
                            </Text>
                        )}
                    </VStack>
                </HStack>
                <VStack w="full" spacing={4} fontSize="sm">
                    <VStack w="full" spacing={2} fontSize="sm">
                        {props.statuses.map((item) => (
                            <HStack
                                key={item.id}
                                align="center"
                                spacing={0}
                                w="full"
                                justify="space-between"
                                color="whiteAlpha.800"
                                borderWidth={2}
                                borderColor="whiteAlpha.200"
                                px={4}
                                py={3}
                                borderRadius="md"
                            >
                                <VStack align="start" spacing={1}>
                                    <HStack spacing={3}>
                                        <Icon
                                            as={item.status.Icon}
                                            fontSize="md"
                                            color={`${item.status.colorScheme}.300`}
                                        />
                                        <Text fontWeight="medium">{item.title}</Text>
                                        <Tooltip
                                            placement="top"
                                            px={2}
                                            py={1}
                                            bg="gray.50"
                                            color="gray.900"
                                            borderRadius="md"
                                            closeOnClick={false}
                                            shouldWrapChildren={true}
                                            label={item.description}
                                        >
                                            <Icon
                                                color="whiteAlpha.600"
                                                as={AiOutlineInfoCircle}
                                            />
                                        </Tooltip>
                                    </HStack>
                                    {/* <Text color="whiteAlpha.600">{item.description}</Text> */}
                                </VStack>
                                <HStack spacing={3} fontSize="xs">
                                    <Text color="whiteAlpha.600" flexShrink={0}>
                                        {item.created.relative}
                                    </Text>
                                    <Avatar
                                        size="xs"
                                        name="Agent Susie"
                                        bg="blue.300"
                                        flexShrink={0}
                                    />
                                </HStack>
                            </HStack>
                        ))}
                    </VStack>
                    {props.truncation && (
                        <HStack w="full" justify="center">
                            <Button
                                {...props.truncation.button}
                                size="sm"
                                color="whiteAlpha.500"
                                bg="none"
                                _hover={{ bg: 'whiteAlpha.50' }}
                            >
                                {props.truncation.label}
                            </Button>
                        </HStack>
                    )}
                </VStack>
            </VStack>
        );
    };

    return (props) => {
        return (
            <VStack w="full" align="start" px={6}>
                <VStack
                    w="full"
                    align="center"
                    pt={{ base: 2, md: 3, lg: 4, xl: 6 }}
                    pb={{ base: 24, xl: 44 }}
                    spacing={{ base: 4, md: 6, lg: 8, xl: 12 }}
                >
                    <HStack w="full" justify="space-between">
                        <Breadcrumb
                            userSelect="none"
                            separator={
                                <Icon as={HiOutlineSlash} color="whiteAlpha.400" />
                            }
                            spacing={0}
                        >
                            <BreadcrumbItem>
                                <BreadcrumbLink
                                    as={Button}
                                    color="whiteAlpha.600"
                                    bg="none"
                                    size="sm"
                                    minH={0}
                                    textDecor="none"
                                    fontWeight="medium"
                                    px={2}
                                    py={0.5}
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                >
                                    Workspace
                                </BreadcrumbLink>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                                <HStack
                                    maxW="20rem"
                                    bg="none"
                                    fontSize="sm"
                                    minH={0}
                                    textDecor="none"
                                    fontWeight="medium"
                                    userSelect="none"
                                    cursor="pointer"
                                    px={2}
                                    py={0.5}
                                    borderRadius="md"
                                    spacing={2}
                                    _hover={{
                                        bg: 'whiteAlpha.50',
                                    }}
                                >
                                    <Icon
                                        fontSize="md"
                                        color="whiteAlpha.600"
                                        as={GoWorkflow}
                                    />
                                    <Text
                                        noOfLines={1}
                                        overflow="hidden"
                                        w="full"
                                        textOverflow="ellipsis"
                                        color="whiteAlpha.600"
                                    >
                                        Tasks
                                    </Text>
                                </HStack>
                            </BreadcrumbItem>
                        </Breadcrumb>
                    </HStack>
                    <VStack maxW="48rem" w="full" align="start">
                        {props.status?.kind === 'empty' && (
                            <Center w="full" h="full">
                                <VStack
                                    w="full"
                                    py={24}
                                    spacing={4}
                                    align="center"
                                    // bg="whiteAlpha.100"
                                    p={4}
                                    borderRadius="lg"
                                    // divider={
                                    //     <StackDivider
                                    //         borderWidth={1}
                                    //         borderColor="whiteAlpha.200"
                                    //     />
                                    // }
                                >
                                    <VStack align="center" spacing={4}>
                                        <Icon
                                            color="whiteAlpha.400"
                                            as={props.status.Icon}
                                            fontSize="4xl"
                                        />
                                        <VStack spacing={1} align="center">
                                            <Text
                                                fontWeight="semibold"
                                                noOfLines={1}
                                                color="whiteAlpha.800"
                                            >
                                                {props.status.label}
                                            </Text>
                                            <Text color="whiteAlpha.600">
                                                {props.status.description}
                                            </Text>
                                        </VStack>
                                    </VStack>
                                    {props.status.action && (
                                        <HStack w="full" justify="center">
                                            <Link {...props.status.action.link}>
                                                <Button
                                                    {...props.status.action.button}
                                                    size="md"
                                                    bg="none"
                                                    borderRadius="full"
                                                    borderWidth={2}
                                                    borderColor="whiteAlpha.200"
                                                    rightIcon={
                                                        <Icon
                                                            fontSize="xl"
                                                            as={BsArrowRightShort}
                                                        />
                                                    }
                                                    _hover={{ bg: 'whiteAlpha.50' }}
                                                >
                                                    {props.status.action.label}
                                                </Button>
                                            </Link>
                                        </HStack>
                                    )}
                                </VStack>
                            </Center>
                        )}
                        {props.items.length > 0 && (
                            <VStack w="full" spacing={12}>
                                {props.items.map((item) => (
                                    <TaskItemContainer
                                        as={TaskItem}
                                        key={item.task.id}
                                        {...item}
                                    />
                                ))}
                            </VStack>
                        )}
                    </VStack>
                </VStack>
            </VStack>
        );
    };
}
